// frontend/src/services/editOvertime.service.ts - UPDATED: Added removeEmployeeFromRequest for quick removal from Reports modal
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
  Timestamp,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  QueryDocumentSnapshot,
  DocumentData
} from 'firebase/firestore';
import { db } from './firebase';
import { createOvertimeService, Employee, Approver, OvertimeProject } from './createOvertime.service';

export interface EditableOvertimeRequest {
  id: string;
  
  // Multi-project support
  projects: OvertimeProject[];
  totalProjects: number;
  
  // Legacy single-project fields
  projectName: string;
  projectCode: string;
  startTime: Date;
  endTime: Date;
  overtimeDate: Date;
  
  // Night shift support
  isNightShift?: boolean;
  hasLongShift?: boolean;
  
  // Shared fields
  employeeIds: string[];
  employeeDetails: Array<{
    id: string;
    name: string;
    designation: string;
    department: string;
    employeeNumber: string;
    pin?: string;
  }>;
  approverEmpId: string;
  approverName: string;
  requesterId: string;
  requesterName: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  requestTime: Date;
  remarks?: string;
  editHistory?: EditHistoryEntry[];
  
  // Edit permission flags
  canEdit: boolean;
  canAddRemark: boolean;
  canEditAfterApproval: boolean;
  requiresReApproval: boolean;
  
  totalEmployees: number;
  totalHours: number;
  
  // Attendance validation
  attendanceValidation?: {
    isValidated: boolean;
    validationPending: boolean;
    validatedAt: Date | null;
    validationResults: Array<{
      employeeId: string;
      employeeName: string;
      employeePin: string;
      hasAttendance: boolean;
      status: 'pending' | 'validated' | 'absent';
      attendanceRecord?: {
        checkIn: string;
        checkOut: string;
        workStatus: string;
      } | null;
      validatedAt: Date | null;
    }>;
    presentCount: number;
    absentCount: number;
    absentEmployeeIds: string[];
    absentEmployeeNames: string[];
    validationScheduledFor: Date;
  };
  willValidateOn?: Date;
}

export interface EditHistoryEntry {
  editedAt: Date;
  editedBy: string;
  editedByName: string;
  changes: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
  reason?: string;
  wasApproved?: boolean;
}

export interface EditOvertimeData {
  requestId: string;
  
  // Support editing multiple projects
  projects?: OvertimeProject[];
  
  // Legacy fields
  projectName?: string;
  projectCode?: string;
  startTime?: Date;
  endTime?: Date;
  overtimeDate?: Date;
  
  // Night shift support
  isNightShift?: boolean;
  
  employeeIds?: string[];
  selectedApproverId?: string;
  selectedApproverName?: string;
  editReason?: string;
  editorId: string;
  editorName: string;
}

export interface EditResult {
  success: boolean;
  message: string;
  updatedRequest?: EditableOvertimeRequest;
  requiresReApproval?: boolean;
}

export interface BulkAddResult {
  success: boolean;
  message: string;
  addedCount: number;
  skippedCount: number;
  addedEmployees: string[];
  skippedEmployees: string[];
}

// 🆕 NEW: Result type for single employee removal
export interface RemoveEmployeeResult {
  success: boolean;
  message: string;
  removedEmployeeId?: string;
  removedEmployeeName?: string;
  remainingCount?: number;
}

class EditOvertimeService {
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_TTL = 2 * 60 * 1000;

  /**
   * Map Firestore document to EditableOvertimeRequest
   */
  private mapToEditableRequest(docSnap: QueryDocumentSnapshot<DocumentData>, userId?: string): EditableOvertimeRequest {
    const data = docSnap.data();
    
    // Parse projects
    let projects: OvertimeProject[] = [];
    
    if (data.projects && Array.isArray(data.projects)) {
      projects = data.projects.map((proj: any) => ({
        id: proj.id || `proj_${Date.now()}`,
        projectName: proj.projectName || '',
        projectCode: proj.projectCode || '',
        startTime: proj.startTime?.toDate() || new Date(),
        endTime: proj.endTime?.toDate() || new Date(),
        duration: proj.duration || 0,
        overtimeDate: proj.overtimeDate?.toDate() || new Date(),
        isNightShift: proj.isNightShift || false,
        hasLongShift: proj.hasLongShift || false
      }));
    } else {
      projects = [{
        id: 'proj_1',
        projectName: data.projectName || '',
        projectCode: data.projectCode || '',
        startTime: data.startTime?.toDate() || new Date(),
        endTime: data.endTime?.toDate() || new Date(),
        duration: data.totalHours || 0,
        overtimeDate: data.overtimeDate?.toDate() || new Date(),
        isNightShift: data.isNightShift || false,
        hasLongShift: data.hasLongShift || false
      }];
    }
    
    // Map attendance validation
    let attendanceValidation: EditableOvertimeRequest['attendanceValidation'] = undefined;
    if (data.attendanceValidation) {
      const av = data.attendanceValidation;
      attendanceValidation = {
        isValidated: av.isValidated || false,
        validationPending: av.validationPending || false,
        validatedAt: av.validatedAt instanceof Timestamp ? av.validatedAt.toDate() : null,
        validationResults: (av.validationResults || []).map((result: any) => ({
          employeeId: result.employeeId,
          employeeName: result.employeeName,
          employeePin: result.employeePin,
          hasAttendance: result.hasAttendance || false,
          status: result.status || 'pending',
          attendanceRecord: result.attendanceRecord || null,
          validatedAt: result.validatedAt instanceof Timestamp ? result.validatedAt.toDate() : null
        })),
        presentCount: av.presentCount || 0,
        absentCount: av.absentCount || 0,
        absentEmployeeIds: av.absentEmployeeIds || [],
        absentEmployeeNames: av.absentEmployeeNames || [],
        validationScheduledFor: av.validationScheduledFor instanceof Timestamp 
          ? av.validationScheduledFor.toDate() 
          : new Date()
      };
    }

    // Determine edit permissions
    const canEdit = userId ? this.canUserEdit(data, userId) : false;
    const canAddRemark = userId ? this.canUserAddRemark(data, userId) : false;

    return {
      id: docSnap.id,
      
      projects: projects,
      totalProjects: projects.length,
      
      projectName: projects[0]?.projectName || '',
      projectCode: projects[0]?.projectCode || '',
      startTime: projects[0]?.startTime || new Date(),
      endTime: projects[0]?.endTime || new Date(),
      overtimeDate: projects[0]?.overtimeDate || new Date(),
      
      isNightShift: data.isNightShift || projects[0]?.isNightShift || false,
      hasLongShift: data.hasLongShift || projects[0]?.hasLongShift || false,
      
      employeeIds: data.employeeIds || [],
      employeeDetails: data.employeeDetails || [],
      approverEmpId: data.approverEmpId || '',
      approverName: data.approverName || '',
      requesterId: data.requesterId || '',
      requesterName: data.requesterName || '',
      status: data.status || 'pending',
      requestTime: data.requestTime?.toDate() || new Date(),
      remarks: data.remarks || '',
      editHistory: this.parseEditHistory(data.editHistory),
      
      canEdit,
      canAddRemark,
      canEditAfterApproval: data.canEditAfterApproval !== false,
      requiresReApproval: data.requiresReApproval || false,
      
      totalEmployees: data.totalEmployees || data.employeeIds?.length || 0,
      totalHours: data.totalHours || 0,
      attendanceValidation,
      willValidateOn: data.willValidateOn instanceof Timestamp ? data.willValidateOn.toDate() : undefined
    };
  }

  /**
   * Get editable overtime request by ID
   */
  async getEditableRequest(requestId: string, userId: string): Promise<EditableOvertimeRequest | null> {
    try {
      console.log('Fetching editable overtime request:', requestId);

      const requestRef = doc(db, 'overtime_requests', requestId);
      const requestDoc = await getDoc(requestRef);

      if (!requestDoc.exists()) {
        console.error('Overtime request not found');
        return null;
      }

      const editableRequest = this.mapToEditableRequest(requestDoc as QueryDocumentSnapshot<DocumentData>, userId);

      console.log('Editable request loaded:', {
        id: editableRequest.id,
        canEdit: editableRequest.canEdit,
        status: editableRequest.status,
        projects: editableRequest.totalProjects,
        isNightShift: editableRequest.isNightShift
      });

      return editableRequest;
    } catch (error) {
      console.error('Error fetching editable request:', error);
      throw error;
    }
  }

  /**
   * Get today's overtime requests
   */
  async getTodayOvertimeRequests(userId: string): Promise<EditableOvertimeRequest[]> {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const todayQuery = query(
        collection(db, 'overtime_requests'),
        where('requesterId', '==', userId),
        where('overtimeDate', '>=', Timestamp.fromDate(today)),
        where('overtimeDate', '<', Timestamp.fromDate(tomorrow)),
        orderBy('overtimeDate', 'asc')
      );

      const snapshot = await getDocs(todayQuery);
      const requests = snapshot.docs.map(doc => this.mapToEditableRequest(doc, userId));
      return requests;
    } catch (error) {
      console.error('Error fetching today requests:', error);
      return [];
    }
  }

  /**
   * Get upcoming overtime requests (next 7 days)
   */
  async getUpcomingOvertimeRequests(userId: string): Promise<EditableOvertimeRequest[]> {
    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      
      const nextWeek = new Date(tomorrow);
      nextWeek.setDate(nextWeek.getDate() + 7);

      const upcomingQuery = query(
        collection(db, 'overtime_requests'),
        where('requesterId', '==', userId),
        where('overtimeDate', '>=', Timestamp.fromDate(tomorrow)),
        where('overtimeDate', '<', Timestamp.fromDate(nextWeek)),
        orderBy('overtimeDate', 'asc'),
        limit(20)
      );

      const snapshot = await getDocs(upcomingQuery);
      const requests = snapshot.docs.map(doc => this.mapToEditableRequest(doc, userId));
      return requests;
    } catch (error) {
      console.error('Error fetching upcoming requests:', error);
      return [];
    }
  }

  /**
   * Get validated overtime requests
   */
  async getValidatedOvertimeRequests(
    requesterId: string,
    lastNDays: number = 7
  ): Promise<EditableOvertimeRequest[]> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - lastNDays);
      cutoffDate.setHours(0, 0, 0, 0);

      const simpleQuery = query(
        collection(db, 'overtime_requests'),
        where('requesterId', '==', requesterId),
        orderBy('overtimeDate', 'desc'),
        limit(30)
      );

      const snapshot = await getDocs(simpleQuery);
      const allRequests = snapshot.docs.map(doc => this.mapToEditableRequest(doc, requesterId));
      
      const validatedRequests = allRequests.filter(req => {
        const validation = req.attendanceValidation;
        if (!validation || !validation.isValidated) return false;
        
        const overtimeDate = new Date(req.overtimeDate);
        overtimeDate.setHours(0, 0, 0, 0);
        return overtimeDate >= cutoffDate;
      }).slice(0, 10);
      
      return validatedRequests;

    } catch (error) {
      console.error('Error loading validated requests:', error);
      return [];
    }
  }

  /**
   * 🆕 NEW: Remove a single employee from an overtime request
   * Only the requester (creator) can remove employees.
   * Works on pending requests. For approved/rejected, resets to pending.
   */
  async removeEmployeeFromRequest(
    requestId: string,
    employeeIdToRemove: string,
    removerId: string,
    removerName: string
  ): Promise<RemoveEmployeeResult> {
    try {
      console.log(`🗑️ Removing employee ${employeeIdToRemove} from request ${requestId}`);

      // 1. Get current request
      const requestRef = doc(db, 'overtime_requests', requestId);
      const requestDoc = await getDoc(requestRef);

      if (!requestDoc.exists()) {
        return { success: false, message: 'Overtime request not found' };
      }

      const data = requestDoc.data();

      // 2. Permission check - only the requester can remove
      if (data.requesterId !== removerId) {
        return { success: false, message: 'Only the request creator can remove employees' };
      }

      // 3. Check request is not cancelled
      if (data.status === 'cancelled') {
        return { success: false, message: 'Cannot modify a cancelled request' };
      }

      // 4. Check employee exists in the request
      const currentEmployeeIds: string[] = data.employeeIds || [];
      if (!currentEmployeeIds.includes(employeeIdToRemove)) {
        return { success: false, message: 'Employee is not in this request' };
      }

      // 5. Prevent removing the last employee
      if (currentEmployeeIds.length <= 1) {
        return { success: false, message: 'Cannot remove the last employee. Delete the request instead.' };
      }

      // 6. Find employee name from employeeDetails
      const employeeDetails: any[] = data.employeeDetails || [];
      const removedEmployee = employeeDetails.find((e: any) => e.id === employeeIdToRemove);
      const removedEmployeeName = removedEmployee?.name || 'Unknown Employee';

      // 7. Build updated arrays
      const updatedEmployeeIds = currentEmployeeIds.filter((id: string) => id !== employeeIdToRemove);
      const updatedEmployeeDetails = employeeDetails.filter((e: any) => e.id !== employeeIdToRemove);

      // 8. Update attendance validation if present
      const updateData: any = {
        employeeIds: updatedEmployeeIds,
        employeeDetails: updatedEmployeeDetails,
        totalEmployees: updatedEmployeeIds.length,
        updatedAt: serverTimestamp(),
        lastEditedAt: serverTimestamp(),
        lastEditedBy: removerId,
        lastEditedByName: removerName
      };

      if (data.attendanceValidation) {
        const av = data.attendanceValidation;
        const updatedValidationResults = (av.validationResults || []).filter(
          (r: any) => r.employeeId !== employeeIdToRemove
        );
        updateData.attendanceValidation = {
          ...av,
          validationResults: updatedValidationResults,
          // Reset validation since employee list changed
          isValidated: false,
          validationPending: true,
          presentCount: 0,
          absentCount: 0,
          absentEmployeeIds: (av.absentEmployeeIds || []).filter((id: string) => id !== employeeIdToRemove),
          absentEmployeeNames: (av.absentEmployeeNames || []).filter((n: string) => n !== removedEmployeeName)
        };
      }

      // 9. Handle re-approval if was approved/rejected
      const wasApproved = data.status === 'approved';
      const wasRejected = data.status === 'rejected';
      const needsReApproval = wasApproved || wasRejected;

      if (needsReApproval) {
        updateData.status = 'pending';
        updateData.requiresReApproval = true;
        updateData.previousStatus = data.status;
        updateData.resetToPendingAt = serverTimestamp();
      }

      // 10. Add to edit history
      const existingHistory = data.editHistory || [];
      const newHistoryEntry = {
        editedAt: Timestamp.fromDate(new Date()),
        editedBy: removerId,
        editedByName: removerName,
        changes: [{
          field: 'employeeIds',
          oldValue: `${currentEmployeeIds.length} employees`,
          newValue: `${updatedEmployeeIds.length} employees (removed ${removedEmployeeName})`
        }],
        reason: `Removed employee: ${removedEmployeeName}`,
        wasApproved: needsReApproval
      };
      updateData.editHistory = [...existingHistory, newHistoryEntry];

      // 11. Save to Firestore
      await updateDoc(requestRef, updateData);

      // 12. Send notification to approver
      await this.sendEditNotification({
        requestId,
        editorName: removerName,
        approverEmpId: data.approverEmpId || '',
        approverName: data.approverName || '',
        projectName: data.projectName || '',
        changes: [{ field: 'employees', oldValue: removedEmployeeName, newValue: 'Removed' }],
        needsReApproval
      });

      console.log(`✅ Employee ${removedEmployeeName} removed from request ${requestId}`);

      return {
        success: true,
        message: needsReApproval
          ? `${removedEmployeeName} removed. Request reset to pending for re-approval.`
          : `${removedEmployeeName} removed successfully.`,
        removedEmployeeId: employeeIdToRemove,
        removedEmployeeName: removedEmployeeName,
        remainingCount: updatedEmployeeIds.length
      };

    } catch (error) {
      console.error('❌ Error removing employee from request:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to remove employee'
      };
    }
  }

  /**
   * FIXED: Edit overtime request with proper night shift support
   */
  async editOvertimeRequest(editData: EditOvertimeData): Promise<EditResult> {
    try {
      console.log('Editing overtime request:', editData.requestId);

      // Get current request
      const currentRequest = await this.getEditableRequest(editData.requestId, editData.editorId);
      
      if (!currentRequest) {
        return { success: false, message: 'Overtime request not found' };
      }

      if (!currentRequest.canEdit) {
        return { success: false, message: 'You do not have permission to edit this request' };
      }

      const wasApproved = currentRequest.status === 'approved';
      const wasRejected = currentRequest.status === 'rejected';
      const needsReApproval = wasApproved || wasRejected;

      // Track changes
      const changes: { field: string; oldValue: any; newValue: any }[] = [];
      const updateData: any = {
        updatedAt: serverTimestamp(),
        lastEditedAt: serverTimestamp(),
        lastEditedBy: editData.editorId,
        lastEditedByName: editData.editorName
      };

      // Handle multi-project edits
      if (editData.projects && editData.projects.length > 0) {
        const projectsForFirestore = editData.projects.map(proj => ({
          id: proj.id,
          projectName: proj.projectName.trim(),
          projectCode: proj.projectCode.trim(),
          startTime: Timestamp.fromDate(proj.startTime),
          endTime: Timestamp.fromDate(proj.endTime),
          duration: proj.duration,
          overtimeDate: Timestamp.fromDate(proj.overtimeDate),
          isNightShift: proj.isNightShift || false,
          hasLongShift: proj.hasLongShift || false
        }));

        changes.push({
          field: 'projects',
          oldValue: `${currentRequest.totalProjects} project(s)`,
          newValue: `${editData.projects.length} project(s)`
        });

        updateData.projects = projectsForFirestore;
        updateData.totalProjects = editData.projects.length;
        
        updateData.projectName = editData.projects[0].projectName.trim();
        updateData.projectCode = editData.projects[0].projectCode.trim();
        updateData.startTime = Timestamp.fromDate(editData.projects[0].startTime);
        updateData.endTime = Timestamp.fromDate(editData.projects[0].endTime);
        updateData.overtimeDate = Timestamp.fromDate(editData.projects[0].overtimeDate);
        updateData.isNightShift = editData.projects[0].isNightShift || false;
        updateData.hasLongShift = editData.projects[0].hasLongShift || false;
        
        const totalHours = editData.projects.reduce((sum, proj) => sum + proj.duration, 0);
        updateData.totalHours = totalHours;
      }

      // Handle legacy single-project edits
      if (editData.projectName && editData.projectName !== currentRequest.projectName) {
        changes.push({ field: 'projectName', oldValue: currentRequest.projectName, newValue: editData.projectName });
        updateData.projectName = editData.projectName;
      }

      if (editData.projectCode && editData.projectCode !== currentRequest.projectCode) {
        changes.push({ field: 'projectCode', oldValue: currentRequest.projectCode, newValue: editData.projectCode });
        updateData.projectCode = editData.projectCode;
      }

      if (editData.startTime && editData.startTime.getTime() !== currentRequest.startTime.getTime()) {
        changes.push({ field: 'startTime', oldValue: currentRequest.startTime.toISOString(), newValue: editData.startTime.toISOString() });
        updateData.startTime = Timestamp.fromDate(editData.startTime);
      }

      if (editData.endTime && editData.endTime.getTime() !== currentRequest.endTime.getTime()) {
        changes.push({ field: 'endTime', oldValue: currentRequest.endTime.toISOString(), newValue: editData.endTime.toISOString() });
        updateData.endTime = Timestamp.fromDate(editData.endTime);
      }

      if (editData.overtimeDate && editData.overtimeDate.getTime() !== currentRequest.overtimeDate.getTime()) {
        changes.push({ field: 'overtimeDate', oldValue: currentRequest.overtimeDate.toISOString().split('T')[0], newValue: editData.overtimeDate.toISOString().split('T')[0] });
        updateData.overtimeDate = Timestamp.fromDate(editData.overtimeDate);
      }

      // Handle night shift flag
      if (editData.isNightShift !== undefined && editData.isNightShift !== currentRequest.isNightShift) {
        changes.push({ field: 'isNightShift', oldValue: currentRequest.isNightShift ? 'Yes' : 'No', newValue: editData.isNightShift ? 'Yes' : 'No' });
        updateData.isNightShift = editData.isNightShift;
        updateData.hasLongShift = editData.isNightShift;
      }

      // Recalculate total hours when time or night shift changes
      if (editData.startTime && editData.endTime) {
        const startMs = editData.startTime.getTime();
        const endMs = editData.endTime.getTime();
        let durationMs = endMs - startMs;
        
        if (durationMs < 0) {
          durationMs += 24 * 60 * 60 * 1000;
        }
        
        const totalHours = durationMs / (1000 * 60 * 60);
        updateData.totalHours = Math.round(totalHours * 10) / 10;
        
        if (!editData.projects && currentRequest.projects?.length > 0) {
          const updatedProjects = currentRequest.projects.map((proj, idx) => {
            if (idx === 0) {
              return {
                ...proj,
                id: proj.id,
                projectName: (editData.projectName || proj.projectName).trim(),
                projectCode: (editData.projectCode || proj.projectCode).trim(),
                startTime: Timestamp.fromDate(editData.startTime!),
                endTime: Timestamp.fromDate(editData.endTime!),
                duration: updateData.totalHours,
                overtimeDate: editData.overtimeDate 
                  ? Timestamp.fromDate(editData.overtimeDate)
                  : Timestamp.fromDate(proj.overtimeDate),
                isNightShift: editData.isNightShift !== undefined ? editData.isNightShift : (proj.isNightShift || false),
                hasLongShift: editData.isNightShift !== undefined ? editData.isNightShift : (proj.hasLongShift || false)
              };
            }
            return {
              ...proj,
              startTime: Timestamp.fromDate(proj.startTime),
              endTime: Timestamp.fromDate(proj.endTime),
              overtimeDate: Timestamp.fromDate(proj.overtimeDate),
              isNightShift: proj.isNightShift || false,
              hasLongShift: proj.hasLongShift || false
            };
          });
          updateData.projects = updatedProjects;
        }
      }

      if (editData.employeeIds && JSON.stringify(editData.employeeIds) !== JSON.stringify(currentRequest.employeeIds)) {
        changes.push({ field: 'employeeIds', oldValue: `${currentRequest.employeeIds.length} employees`, newValue: `${editData.employeeIds.length} employees` });
        updateData.employeeIds = editData.employeeIds;
        updateData.totalEmployees = editData.employeeIds.length;

        const employeeDetails = await this.getEmployeeDetails(editData.employeeIds);
        updateData.employeeDetails = employeeDetails;
        
        if (currentRequest.attendanceValidation) {
          const existingResults = currentRequest.attendanceValidation.validationResults || [];
          const existingEmployeeIds = existingResults.map(r => r.employeeId);
          
          const updatedResults = existingResults.filter(r => 
            editData.employeeIds!.includes(r.employeeId)
          );
          
          const newEmployeeIds = editData.employeeIds.filter(id => !existingEmployeeIds.includes(id));
          for (const empId of newEmployeeIds) {
            const emp = employeeDetails.find(e => e.id === empId);
            updatedResults.push({
              employeeId: empId,
              employeeName: emp?.name || 'Unknown',
              employeePin: emp?.employeeNumber || '',
              hasAttendance: false,
              status: 'pending',
              attendanceRecord: null,
              validatedAt: null
            });
          }
          
          updateData.attendanceValidation = {
            isValidated: false,
            validationPending: true,
            validatedAt: null,
            validationResults: updatedResults,
            presentCount: 0,
            absentCount: 0,
            absentEmployeeIds: [],
            absentEmployeeNames: [],
            validationScheduledFor: currentRequest.attendanceValidation.validationScheduledFor 
              ? Timestamp.fromDate(currentRequest.attendanceValidation.validationScheduledFor)
              : Timestamp.fromDate(new Date())
          };
        }
      }

      if (editData.selectedApproverId && editData.selectedApproverId !== currentRequest.approverEmpId) {
        changes.push({ field: 'approver', oldValue: currentRequest.approverName, newValue: editData.selectedApproverName || 'New Approver' });
        updateData.approverEmpId = editData.selectedApproverId;
        updateData.approverName = editData.selectedApproverName;
      }

      if (changes.length === 0) {
        return { success: false, message: 'No changes detected' };
      }

      // Reset status if was approved/rejected
      if (needsReApproval) {
        updateData.status = 'pending';
        updateData.requiresReApproval = true;
        updateData.previousStatus = currentRequest.status;
        updateData.resetToPendingAt = serverTimestamp();
        changes.push({ field: 'status', oldValue: currentRequest.status, newValue: 'pending (requires re-approval)' });
      }

      // Build edit history entry
      const now = new Date();
      
      const newEditHistoryEntry = {
        editedAt: Timestamp.fromDate(now),
        editedBy: editData.editorId,
        editedByName: editData.editorName,
        changes: changes,
        reason: editData.editReason || 'Request updated',
        wasApproved: needsReApproval
      };

      const existingHistory = (currentRequest.editHistory || []).map(entry => ({
        editedAt: Timestamp.fromDate(entry.editedAt),
        editedBy: entry.editedBy,
        editedByName: entry.editedByName,
        changes: entry.changes,
        reason: entry.reason || '',
        wasApproved: entry.wasApproved || false
      }));

      updateData.editHistory = [...existingHistory, newEditHistoryEntry];

      // Update the document
      const requestRef = doc(db, 'overtime_requests', editData.requestId);
      await updateDoc(requestRef, updateData);

      // Send notifications
      await this.sendEditNotification({
        requestId: editData.requestId,
        editorName: editData.editorName,
        approverEmpId: currentRequest.approverEmpId,
        approverName: currentRequest.approverName,
        projectName: updateData.projectName || currentRequest.projectName,
        changes,
        needsReApproval
      });

      console.log('Overtime request updated successfully');

      const updatedRequest = await this.getEditableRequest(editData.requestId, editData.editorId);

      return {
        success: true,
        message: needsReApproval
          ? `Request updated and reset to pending. ${changes.length} change(s) made. Re-approval required.`
          : `Request updated successfully. ${changes.length} change(s) made.`,
        updatedRequest: updatedRequest || undefined,
        requiresReApproval: needsReApproval
      };

    } catch (error) {
      console.error('Error editing overtime request:', error);
      return { success: false, message: error instanceof Error ? error.message : 'Failed to edit overtime request' };
    }
  }

  /**
   * Add employees to existing request (bulk add)
   */
  async addEmployeesToRequest(
    requestId: string, 
    newEmployeeIds: string[], 
    editorId: string, 
    editorName: string
  ): Promise<BulkAddResult> {
    try {
      const currentRequest = await this.getEditableRequest(requestId, editorId);
      
      if (!currentRequest) {
        return { 
          success: false, 
          message: 'Overtime request not found',
          addedCount: 0, skippedCount: 0,
          addedEmployees: [], skippedEmployees: []
        };
      }

      if (!currentRequest.canEdit) {
        return { 
          success: false, 
          message: 'You do not have permission to edit this request',
          addedCount: 0, skippedCount: 0,
          addedEmployees: [], skippedEmployees: []
        };
      }

      const existingIds = new Set(currentRequest.employeeIds);
      const uniqueNewIds: string[] = [];
      const skippedIds: string[] = [];
      
      for (const id of newEmployeeIds) {
        if (existingIds.has(id)) {
          skippedIds.push(id);
        } else {
          uniqueNewIds.push(id);
          existingIds.add(id);
        }
      }

      if (uniqueNewIds.length === 0) {
        return { 
          success: true, 
          message: `All ${skippedIds.length} employee(s) were already in this request`,
          addedCount: 0, skippedCount: skippedIds.length,
          addedEmployees: [], skippedEmployees: skippedIds
        };
      }

      const combinedEmployeeIds = [...currentRequest.employeeIds, ...uniqueNewIds];

      const editResult = await this.editOvertimeRequest({
        requestId,
        employeeIds: combinedEmployeeIds,
        editorId,
        editorName,
        editReason: `Added ${uniqueNewIds.length} employee(s) via bulk upload${skippedIds.length > 0 ? ` (${skippedIds.length} duplicates skipped)` : ''}`
      });

      return {
        success: editResult.success,
        message: editResult.success 
          ? `Added ${uniqueNewIds.length} employee(s)${skippedIds.length > 0 ? `, ${skippedIds.length} already existed` : ''}`
          : editResult.message,
        addedCount: uniqueNewIds.length,
        skippedCount: skippedIds.length,
        addedEmployees: uniqueNewIds,
        skippedEmployees: skippedIds
      };

    } catch (error) {
      console.error('Error adding employees:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Failed to add employees',
        addedCount: 0, skippedCount: 0,
        addedEmployees: [], skippedEmployees: []
      };
    }
  }

  /**
   * Add remark
   */
  async addRemark(requestId: string, remark: string, userId: string, userName: string): Promise<EditResult> {
    try {
      const requestRef = doc(db, 'overtime_requests', requestId);
      const requestDoc = await getDoc(requestRef);

      if (!requestDoc.exists()) {
        return { success: false, message: 'Overtime request not found' };
      }

      const data = requestDoc.data();

      if (!this.canUserAddRemark(data, userId)) {
        return { success: false, message: 'You do not have permission to add remarks' };
      }

      const currentRemarks = data.remarks || '';
      const timestamp = new Date().toLocaleString();
      const newRemark = `[${timestamp}] ${userName}: ${remark}`;
      const updatedRemarks = currentRemarks ? `${currentRemarks}\n\n${newRemark}` : newRemark;

      await updateDoc(requestRef, {
        remarks: updatedRemarks,
        updatedAt: serverTimestamp(),
        lastRemarkBy: userId,
        lastRemarkByName: userName,
        lastRemarkAt: serverTimestamp()
      });

      return { success: true, message: 'Remark added successfully' };

    } catch (error) {
      console.error('Error adding remark:', error);
      return { success: false, message: error instanceof Error ? error.message : 'Failed to add remark' };
    }
  }

  /**
   * Delete/Cancel overtime request
   */
  async deleteOvertimeRequest(requestId: string, userId: string, userName: string, reason: string): Promise<EditResult> {
    try {
      const currentRequest = await this.getEditableRequest(requestId, userId);
      
      if (!currentRequest) {
        return { success: false, message: 'Overtime request not found' };
      }

      if (currentRequest.requesterId !== userId) {
        return { success: false, message: 'You can only cancel your own requests' };
      }

      const requestRef = doc(db, 'overtime_requests', requestId);
      await updateDoc(requestRef, {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        cancelledBy: userId,
        cancelledByName: userName,
        cancellationReason: reason,
        updatedAt: serverTimestamp()
      });

      return { success: true, message: 'Overtime request cancelled successfully' };

    } catch (error) {
      console.error('Error cancelling request:', error);
      return { success: false, message: error instanceof Error ? error.message : 'Failed to cancel request' };
    }
  }

  private canUserEdit(requestData: any, userId: string): boolean {
    if (requestData.requesterId !== userId) return false;
    if (requestData.status === 'cancelled') return false;
    return true;
  }

  private canUserAddRemark(requestData: any, userId: string): boolean {
    return requestData.requesterId === userId;
  }

  private parseEditHistory(historyData: any[]): EditHistoryEntry[] {
    if (!historyData || !Array.isArray(historyData)) return [];

    return historyData.map(entry => ({
      editedAt: entry.editedAt?.toDate?.() || entry.editedAt || new Date(),
      editedBy: entry.editedBy || '',
      editedByName: entry.editedByName || '',
      changes: entry.changes || [],
      reason: entry.reason || '',
      wasApproved: entry.wasApproved || false
    }));
  }

  private async getEmployeeDetails(employeeIds: string[]): Promise<Array<{
    id: string;
    name: string;
    designation: string;
    department: string;
    employeeNumber: string;
    pin?: string;
  }>> {
    try {
      const employees = await createOvertimeService.getEligibleEmployees();
      return employeeIds
        .map(id => employees.find(emp => emp.id === id))
        .filter(emp => emp !== undefined)
        .map(emp => ({
          id: emp!.id,
          name: emp!.name,
          designation: emp!.designation,
          department: emp!.department,
          employeeNumber: emp!.employeeNumber || emp!.id,
          pin: emp!.pin || ''
        }));
    } catch (error) {
      console.error('Error getting employee details:', error);
      return [];
    }
  }

  private async sendEditNotification(data: {
    requestId: string;
    editorName: string;
    approverEmpId: string;
    approverName: string;
    projectName: string;
    changes: any[];
    needsReApproval: boolean;
  }): Promise<void> {
    try {
      const changesText = data.changes.map(c => c.field).join(', ');
      
      const notificationBody = data.needsReApproval
        ? `${data.editorName} edited overtime request for ${data.projectName}. Changes: ${changesText}. Re-approval required.`
        : `${data.editorName} edited overtime request for ${data.projectName}. Changes: ${changesText}`;
      
      await addDoc(collection(db, 'notifications'), {
        type: data.needsReApproval ? 'overtime_request_reapproval' : 'overtime_request_edited',
        targetUserId: data.approverEmpId,
        title: data.needsReApproval ? 'Overtime Re-Approval Required' : 'Overtime Request Updated',
        body: notificationBody,
        data: {
          requestId: data.requestId,
          projectName: data.projectName,
          editorName: data.editorName,
          changes: changesText,
          needsReApproval: data.needsReApproval.toString()
        },
        timestamp: serverTimestamp(),
        processed: false,
        priority: data.needsReApproval ? 'high' : 'normal'
      });

    } catch (error) {
      console.error('Error sending edit notification:', error);
    }
  }

  clearCache(): void {
    this.cache.clear();
  }
}

export const editOvertimeService = new EditOvertimeService();